"use strict";

export let scoreIsMissing = (result) => result.score == null;

export function topSorted(objects, sortColumn, limit = 3) {
  const VALUE_GETTERS = {
    _raw: (d) => d,
    name: (d) => d.name,
    essential: (d) => d._parent.name,
    respondent: (d) => d.subject,
    change: (d) => d._results[resultIdx]._change,
    score: (d) => d._results[resultIdx].score,
    difficulty: (d) => d.difficulty,
    slug: (d) => d.slug,
    __default__: (d) => d[key],
  };
  const BACKUP_VALUE_GETTERS = {
    // for when main value "ties"
    name: "score",
    essential: "score",
    respondent: "score",
    change: "score",
    score: "name",
    difficulty: "slug",
    __default__: "name",
  };

  let [signKey, resultIdx = "latest"] = sortColumn.split("@");
  let key = signKey.slice(1);
  let sign = sortColumn[0] === "+" ? +1 : -1;
  let getVal = VALUE_GETTERS[key] || VALUE_GETTERS.__default__;
  let bkpGet =
    VALUE_GETTERS[
      BACKUP_VALUE_GETTERS[key] || BACKUP_VALUE_GETTERS.__default__
    ];

  let objectsToSort;
  if (!limit) {
    limit = Number.POSITIVE_INFINITY;
    // preserve all values, but avoid mutating original
    objectsToSort = objects.slice();
  } else {
    // when limiting the output, avoid all N/A values
    objectsToSort = objects.filter((d) => getVal(d) != null);
    if (key === "change") {
      // when showing improvements, avoid showing negative results, and v.v.
      objectsToSort = objectsToSort.filter((d) => sign * getVal(d) < 0);
    }
  }

  let cmp = (a, b) => (a < b ? -sign : a > b ? sign : 0);
  let asc = (a, b) => (a < b ? -1 : a > b ? +1 : 0); // backup/secondary sort makes more sense always ascending
  var sorted = objectsToSort.sort(
    (a, b) => cmp(getVal(a), getVal(b)) || asc(bkpGet(a), bkpGet(b)),
  );

  // deal with "tied scores", see https://trello.com/c/Jc2yigIg/89-3-provide-fancier-logic-in-filter-sorting-for-ties
  let tieGroupingApplies =
    key === "score" || key === "change" || key === "_raw";
  if (tieGroupingApplies && limit < Number.POSITIVE_INFINITY) {
    // group scores by tied values, e.g. compute `[3, 4, 1, 2, 1]` from `[70, 70, 70, 65, 65, 65, 65, 60, 40, 40, 30]`
    let valCounts = handyArray([]),
      _vLast = null;
    sorted.map(getVal).forEach((v) => {
      if (v !== _vLast) valCounts.push(0);
      ++valCounts.last;
      _vLast = v;
    });

    // allow each group of values through only if it fits within the limit as a whole
    let adjustedLimit = 0;
    if (limit === -1)
      adjustedLimit = valCounts[0]; // special case: gather all ties for "top" place
    else
      valCounts.some((n) => {
        let proposedLimit = adjustedLimit + n;
        if (proposedLimit <= limit) adjustedLimit = proposedLimit;
        else return true; // stops iteration once limit reached
      });

    if (adjustedLimit) limit = adjustedLimit;
    // when no groups fit; keep the original limit
  }

  limit = Math.min(limit, sorted.length); // WORKAROUND: Prince XML 13.1 does not like slicing to Infinity!
  return sorted.slice(0, limit);
}

if (process.env.NODE_ENV === "development") {
  // test the topSorted tie logic
  let arr = [70, 70, 70, 65, 65, 65, 65, 60, 40, 40, 30];
  let expectedLengths = [
    arr.length,
    1,
    2,
    3,
    3,
    3,
    3,
    7,
    8,
    8,
    10,
    11,
    arr.length,
    arr.length,
  ];
  expectedLengths.forEach((expectedLength, n) => {
    let result = topSorted(arr, "-_raw", n);
    //console.log(`Result for limit=${n}:`, result)
    if (result.length !== expectedLength)
      throw Error("Issue in topSorted algorithm!");
  });

  let specialResult = topSorted(arr, "-_raw", -1);
  if (specialResult.length !== 3)
    throw Error("Issue with topSorted tie-grouping option!");
}

export function linearTransform([src0, src1], [dst0, dst1]) {
  const m = (dst1 - dst0) / (src1 - src0);
  return (x) => (x - src0) * m + dst0;
}

export function timedRamp(p1, p2) {
  let fade = linearTransform([p1, p2], [0, 1]);
  return (p) => Math.max(0, Math.min(fade(p), 1));
}

export function simpleTitleCase(str) {
  // HT: http://stackoverflow.com/a/196991/179583
  return str.replace(
    /\b\w+/g,
    (s) => s.charAt(0).toUpperCase() + s.substr(1).toLowerCase(),
  );
}

export function plural(count, plural = "s", singular = "") {
  return count === 1 ? singular : plural;
}

export function verboseTimestamp(t) {
  // following "June 27, 2017 at 10:03 am" format of mockup
  let MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  let _h = t.getHours();
  let h = _h % 12;
  if (h === 0) h = 12;
  let ampm = _h > 11 ? "pm" : "am";
  let _m = t.getMinutes();
  let m = _m < 10 ? "0" + _m : "" + _m;
  return `${
    MONTHS[t.getMonth()]
  } ${t.getDate()}, ${t.getFullYear()} at ${h}:${m} ${ampm}`;
}

// simple string-to-anything mapping dictionary
export function BareObject() {
  return Object.create(null);
}

// tallies up how many "truthy" values are within the array
export function countTruthyValues(arr) {
  return arr.reduce((sum, active) => (active ? sum + 1 : sum), 0);
}

export function handyArray(arr, prop = "last") {
  Object.defineProperty(arr, prop, {
    get() {
      return this[this.length - 1];
    },
    set(v) {
      this[this.length - 1] = v;
    },
  });
  return arr;
}

export function reduceToObject(arr, fn) {
  return arr.reduce(function (obj, d, idx) {
    let [k, v] = fn(d, idx);
    obj[k] = v;
    return obj;
  }, new BareObject());
}

export function reduceToArrays(arr, fn) {
  return arr.reduce(function (obj, d, idx) {
    let [k, v] = fn(d, idx);
    if (k in obj) obj[k].push(v);
    else obj[k] = [v];
    return obj;
  }, new BareObject());
}

export function mapObject(obj, fn) {
  return reduceToObject(Object.entries(obj), ([k, v]) => [k, fn(v, k)]);
}

export function twoWayKeyMapping(src) {
  // for dictionaries of form {1:"one_string", 2:"Another"}
  let dst = {};
  Object.keys(src).forEach((k) => {
    let v = src[k].toLowerCase();
    dst[k] = v;
    dst[v] = +k; // HACK: also put the reverse mapping here for convenience
  });
  return dst;
}

export function twoWayMapping(src) {
  // for dictionaries of form {"oneA":"oneB", "twoA":"twoB"}
  let dst = { ...src };
  // assign the reverse mappings
  Object.keys(src).forEach((srcK) => {
    let dstK = src[srcK];
    if (dstK in dst && dst[dstK] !== srcK)
      throw Error(
        `Two-way mapping conflict: ${srcK} was already both a key and value`,
      );
    dst[dstK] = srcK;
  });
  return dst;
}

// NOTE: this does not [currently] handle nested scrolls!
// TODO: migrate callers to new scrollIntoView port below
export function scrollIntoViewIfNeeded(el, opts) {
  if (typeof el === "string") {
    el = document.querySelector(el);
  }
  // c.f. `$.fn.cs_scrollIntoView` in college-success-back/frontend/static/frontend/custom.js
  // …and `inq.utils.scrollParents` in readi-sensemaker-prototype/js/utils/smooth_scroll.js
  var elBox = el.getBoundingClientRect(),
    viewTop = 0,
    viewBottom = window.innerHeight;
  if (elBox.top < viewTop || elBox.bottom > viewBottom) {
    el.scrollIntoView(opts);
  }
}

// stand-in for `React.createRef()` of later versions
export function createRef() {
  let ref = (newVal) => {
    ref.current = newVal;
  };
  ref.current = null;
  return ref;
}

export function escapeHTML(s) {
  // HT: https://stackoverflow.com/questions/3043775/how-to-escape-html/3043791#3043791
  //     referencing `entityify` of https://crockford.com/javascript/remedial.html
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ported from college-success-back $.fn.cs_scrollIntoView
export function scrollIntoView(el, opts) {
  if (typeof el === "string") {
    el = document.querySelector(el);
  }
  opts = {
    instant: false,
    minimalMotion: false,
    padding: 0,
    whenDone: null,
    shouldContinue: null,
    ...opts,
  };

  let step = opts.shouldContinue
    ? function () {
        let wanted = opts.shouldContinue();
        if (!wanted) $(this).stop();
      }
    : null;

  if (opts.minimalMotion) {
    var elBox = el.getBoundingClientRect(),
      viewTop = opts.padding,
      viewBottom = window.innerHeight - opts.padding, // NOTE: already inset w/padding
      topVisible = elBox.top >= viewTop && elBox.top <= viewBottom,
      bottomVisible = elBox.bottom >= viewTop && elBox.bottom <= viewBottom;
    if (topVisible && bottomVisible)
      return; // scrolling not expected to help
    else if (elBox.bottom >= viewBottom) {
      // Target is below the screen, so we want to align it to padded viewBottom. So we compensate padding
      // leaving `bottom = finalPadding + height` effected below, versus the `bottom = viewBottom` desired.
      opts.padding = Math.max(opts.padding, viewBottom - elBox.height); // (if box is too tall, keep original top padding)
    }
  }
  if (opts.instant)
    el.scrollIntoView(); // NOTE: this ignores padding/minimalMotion options
  else {
    // WARNING: assumes that only the body/html element scrolls, no nestedness!
    // TODO: migrate this off of jQuery…using for now since expedient and already leftover on page
    var newTop = $(el).offset().top;
    newTop -= opts.padding;
    $(scrollIntoView.scrollEl)
      .stop(true)
      .animate({ scrollTop: newTop }, { always: opts.whenDone, step });
  }
}
if (typeof document !== "undefined") {
  scrollIntoView.scrollEl =
    document.scrollingElement || document.documentElement || document.body;
}

export function getCsrfToken() {
  function getCookie(name) {
    // simplified from https://docs.djangoproject.com/en/1.8/ref/csrf/#ajax
    if (document.cookie) {
      var cookies = document.cookie.split(";");
      for (var i = 0; i < cookies.length; i++) {
        var cookie = $.trim(cookies[i]);
        if (cookie.substring(0, name.length + 1) === name + "=") {
          return decodeURIComponent(cookie.substring(name.length + 1));
        }
      }
    }
  }
  return getCookie("csrftoken");
}

export function setupAjaxDefaults({ user }) {
  $.ajaxSetup({
    timeout: 30e3,
    beforeSend: (jqXHR, opts) => {
      if (opts.type !== "GET") {
        jqXHR.setRequestHeader("X-CSRFToken", getCsrfToken());
        if (user) jqXHR.setRequestHeader("X-ExpectedUser", user.username);
      }
    },
  });
}

// c.f. `makeReportStorage` in college-success-back's custom.js
export function makePrefixedStorage(...parts) {
  let pre = [...parts].join("/") + "|";
  return {
    get(key, def) {
      let v = localStorage[pre + key];
      if (typeof v === "string")
        try {
          return JSON.parse(v);
        } catch (e) {}
      return def;
    },
    set(key, val) {
      localStorage[pre + key] = JSON.stringify(val);
    },
    del(key) {
      delete localStorage[pre + key];
    },
  };
}

export let basicReducer = (actionKey, initialState, extras = null) =>
  function (state = initialState, action) {
    if (extras !== null) {
      let result;
      if (typeof extras === "function") {
        result = extras.length === 1 ? extras(action) : extras(state, action);
      } else {
        result = extras[action.type];
      }
      if (result !== undefined) return result;
    }
    switch (action.type) {
      case actionKey:
        return action.payload;
      default:
        return state;
    }
  };
export let basicAction = (type) => (payload) => ({ type, payload });
